import { z } from 'zod';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import {
  FileAction,
  requiredFileFieldSchema,
} from '@/schemas/fileFieldSchema.ts';
import { getVariableLabel } from '@/components/richText/templateVariableDisplayNames.ts';
import { getMissingRequiredKeys } from '@/features/dashboard/messageTemplates/types/templateVariables.utils.ts';

const templateTypeSchema = z.object({
  name: z
    .string()
    .min(1, 'Nazwa jest wymagana')
    .max(255, 'Nazwa może mieć max 255 znaków'),
  type: z.literal(ConsentType.TEMPLATE),
  documentTemplate: z.string().min(1, 'Treść szablonu jest wymagana'),
});

const linkTypeSchema = z.object({
  name: z
    .string()
    .min(1, 'Nazwa jest wymagana')
    .max(255, 'Nazwa może mieć max 255 znaków'),
  type: z.literal(ConsentType.LINK),
  linkUrl: z
    .string()
    .min(1, 'Link jest wymagany')
    .regex(
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/\S*)?$/,
      'Podaj poprawny adres URL',
    ),
});

const fileTypeSchema = z.object({
  name: z
    .string()
    .min(1, 'Nazwa jest wymagana')
    .max(255, 'Nazwa może mieć max 255 znaków'),
  type: z.literal(ConsentType.FILE),
  consentFile: requiredFileFieldSchema,
});

export const consentTemplateSchema = z.discriminatedUnion('type', [
  templateTypeSchema,
  linkTypeSchema,
  fileTypeSchema,
]);

/**
 * Schema that additionally enforces the backend `TEMPLATE_MISSING_REQUIRED_VARIABLE`
 * rule on the TEMPLATE document body — every required variable must be output as
 * `{{ key }}`. `requiredKeys` comes from `useGetConsentTemplateVariables`.
 */
export const createConsentTemplateSchema = (requiredKeys: string[]) =>
  consentTemplateSchema.superRefine((data, ctx) => {
    if (data.type !== ConsentType.TEMPLATE) return;
    const missing = getMissingRequiredKeys(data.documentTemplate, requiredKeys);
    if (missing.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['documentTemplate'],
        message: `Brakuje wymaganych zmiennych w treści: ${missing
          .map(getVariableLabel)
          .join(', ')}`,
      });
    }
  });

export type ConsentTemplateFormData = z.infer<typeof consentTemplateSchema>;
export type ConsentTemplateInputData = z.input<typeof consentTemplateSchema>;

/**
 * Formularz montuje się dopiero po wyborze rodzaju, więc wartości domyślne
 * dotyczą jednego wariantu unii — nie ma stanu, w którym pola nieużywanego
 * rodzaju wiszą w formularzu i psują walidację po przełączeniu.
 */
export const consentTemplateDefaultValues = (
  type: ConsentType,
): ConsentTemplateInputData => {
  switch (type) {
    case ConsentType.TEMPLATE:
      return { name: '', type, documentTemplate: '' };
    case ConsentType.LINK:
      return { name: '', type, linkUrl: '' };
    case ConsentType.FILE:
      return { name: '', type, consentFile: { action: FileAction.NO_ACTION } };
  }
};
